/**
 * PlyxCelebration — global 1.5s celebration overlay that mounts once at
 * the Plyx app shell and listens for the `plyx-celebrate` event.
 *
 * Triggered via:
 *   import { celebratePlyx } from "@/components/plyx/PlyxCelebration";
 *   celebratePlyx({ message: "Posted on Plyx ✨" });
 *
 * Renders:
 *   • Translucent dark backdrop (no pointer-events block — page stays
 *     interactive even during the celebration).
 *   • Centered gradient wordmark + message, scaling-in + fading.
 *   • Twelve sparkle particles bursting outward from the centre with
 *     randomised angles + distances for organic motion.
 *
 * Honours `prefers-reduced-motion` by collapsing into a tiny inline
 * toast that fades in/out without the particle burst.
 */
import React, { useEffect, useMemo, useState } from "react";

const PARTICLE_COUNT = 12;
const DEFAULT_DURATION_MS = 1500;

export const celebratePlyx = (opts = {}) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("plyx-celebrate", { detail: opts }));
};

const PlyxCelebration = () => {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState("Posted on Plyx");
  const [theme, setTheme] = useState("default");

  useEffect(() => {
    const handler = (ev) => {
      const detail = ev.detail || {};
      setMessage(detail.message || "Posted on Plyx");
      setTheme(detail.theme || "default");
      setActive(false);
      // Re-trigger the CSS animations on a fresh mount via a microtask.
      requestAnimationFrame(() => setActive(true));
      const t = setTimeout(() => setActive(false), detail.duration || DEFAULT_DURATION_MS);
      return () => clearTimeout(t);
    };
    window.addEventListener("plyx-celebrate", handler);
    return () => window.removeEventListener("plyx-celebrate", handler);
  }, []);

  // ── Palette per theme ──────────────────────────────────────────────
  // `gold` is reserved for like-milestone bursts (100, 1k, 10k, …) so
  // those moments feel distinctly different from a generic confirmation
  // burst. We oversample gold/amber to push the visual reading toward
  // "gold burst" rather than the rainbow default.
  const palette = theme === "gold"
    ? ["#FFB42D", "#FFD15C", "#FF8A1F", "#FFFFFF"]
    : ["#FF2D7A", "#FFB42D", "#00E0C7", "#FFFFFF"];

  // Pre-compute particle trajectories once so each burst has stable
  // angles for its lifetime (avoids re-randomising on each re-render
  // mid-animation, which would visually jitter).
  const particles = useMemo(() => Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    const distance = 90 + Math.random() * 70;
    return {
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance,
      delay: Math.random() * 0.12,
      size: 6 + Math.random() * 6,
      hue: palette[i % palette.length],
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [active, theme]); // re-randomise per burst

  if (!active) return null;

  return (
    <div
      data-testid="plyx-celebration"
      className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center"
    >
      <div className="plyx-celebration-backdrop absolute inset-0" />
      <div className="plyx-celebration-wrap relative">
        {particles.map((p, idx) => (
          <span
            key={`${idx}-${active}`}
            className="plyx-celebration-particle"
            style={{
              "--tx": `${p.tx}px`,
              "--ty": `${p.ty}px`,
              "--delay": `${p.delay}s`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: p.hue,
              boxShadow: `0 0 8px ${p.hue}`,
            }}
          />
        ))}
        <div className="plyx-celebration-bubble">
          <div className="plyx-celebration-wordmark">PLYX</div>
          <div className="plyx-celebration-msg">{message}</div>
        </div>
      </div>
    </div>
  );
};

export default PlyxCelebration;
